import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { ApiEventBus, ApiEventType, TokenInfo, getToken, setToken, removeToken } from '@/api/http';
import { AuthApi, LoginCallbackRequest, PointInfo, UserInfo } from '@/api/auth';
import { TaskDetail } from '@/api/task';
import router from '@/router';

const USER_INFO_KEY = 'user_info';


export const useUserStore = defineStore('user', () => {
  const userInfo = ref<UserInfo | null>(null);
  const images = ref<TaskDetail[]>([]);
  const videos = ref<TaskDetail[]>([]);
  const tokenInfo = ref<TokenInfo | null>(null);
  const isLoading = ref<boolean>(false);
  const error = ref<string | null>(null);
  
  const isAuthenticated = computed(() => !!tokenInfo.value?.access_token && !!userInfo.value);

  const initialize = () => {
    const savedUserInfo = localStorage.getItem(USER_INFO_KEY);
    if (savedUserInfo) {
      try {
        userInfo.value = JSON.parse(savedUserInfo);
      } catch (e) {
        console.error('initialize error, savedUserInfo:', savedUserInfo);
        localStorage.removeItem(USER_INFO_KEY);
      }
    }
    const savedTokenInfo = getToken();
    if (savedTokenInfo) {
      try {
        tokenInfo.value = savedTokenInfo;
      } catch (e) {
        removeToken();
      }
    }
  };
  
  const setUserInfo = (info: UserInfo | null) => {
    if (info) {
      userInfo.value = info;
      console.log('setUserInfo, info:', userInfo.value);
      localStorage.setItem(USER_INFO_KEY, JSON.stringify(info));
    } else {
      localStorage.removeItem(USER_INFO_KEY);
    }
  };

  const getUserInfo = () => {
    if (userInfo.value) {
      console.log('getUserInfo, userInfo.value:', userInfo.value);
      return userInfo.value;
    } else {
      const savedUserInfo = localStorage.getItem(USER_INFO_KEY);
      if (savedUserInfo) {
        console.log('getUserInfo, savedUserInfo:', savedUserInfo);
        return JSON.parse(savedUserInfo);
      }
      return null;
    }
  }

  const loginCallback = async (code: string, state: string) => {
    try {
      isLoading.value = true;
      const request: LoginCallbackRequest = {
        code: code,
        state: state,
      };
      const response = await AuthApi.handleCallback(request);
      if (response.code === 0 && response.data) {
        response.data.detail.avatar = response.data.detail.avatar.replace('=s96-c', '=s96-c-no');
        setUserInfo(response.data.detail); 
        setToken(response.data.token);
        isLoading.value = false;
      }else{
        error.value = response.msg;
        isLoading.value = false;
        forceLogout(); 
        return false;
      }
      return true;
    } catch (err: any) {
      console.error('loginCallback error:', err);
      error.value = err.message;
      isLoading.value = false;
      forceLogout(); 
      return false;
    }
  }
  
  const fetchUserInfo = async (): Promise<boolean> => {
    console.log('fetchUserInfo, tokenInfo.value:', tokenInfo.value);
    if (!tokenInfo.value) return false;
    isLoading.value = true;
    error.value = null;
    try {
      const response = await AuthApi.getUserInfo(); 
      console.log('fetchUserInfo, response:', response);
      if (response.code === 0 && response.data) {
        response.data.detail.avatar = response.data.detail.avatar.replace('=s96-c', '=s96-c-no');
        setUserInfo(response.data.detail); 
        return true;
      } else {
        console.log('fetchUserInfo, response.code:', response.code);
        error.value = response.msg;
        if (response.code === 1001 || response.code === 1002) { 
           clearUserState(); 
        }
        return false;
      }
    } catch (err: any) {
      console.error('getUserInfo error:', err);
      error.value = err.message;
      clearUserState(); 
      return false;
    } finally {
      isLoading.value = false;
    }
  };
  
  // 登出
  const logout = async () => {
    try {
      if (tokenInfo.value) {
        await AuthApi.logout();
      }
    } catch (err) {
      console.error('logout error:', err);
    } finally {
      clearUserState();
    }
  };
  
  // 清除用户状态
  const clearUserState = () => {
    console.log('clearUserState, userInfo.value:', userInfo.value);
    userInfo.value = null;
    tokenInfo.value = null;
    localStorage.removeItem(USER_INFO_KEY);
    removeToken();
    
    // 使用Vue Router跳转到登录页
    if (router && router.currentRoute.value.path !== '/login') {
      router.push('/login').catch(err => {
         console.error("Router push to /login failed:", err);
      });
    }
  };
  
  const forceLogout = () => {
      clearUserState();
  };
  
  const setupAuthListeners = () => {
    ApiEventBus.on(ApiEventType.UNAUTHORIZED, () => {
      forceLogout(); 
    });
    
    ApiEventBus.on(ApiEventType.TOKEN_REFRESH, (info: TokenInfo) => {
      if (info) {
        setToken(info);
      }
    });
    
  };
  
  const refreshPoints = (pointInfo: PointInfo) => {
    if (userInfo.value) {
      userInfo.value.points = pointInfo;
    }
  }
  const refreshUserInfo = (newUserInfo: UserInfo) => {
    userInfo.value = newUserInfo;
    localStorage.setItem(USER_INFO_KEY, JSON.stringify(newUserInfo));
  }

  setupAuthListeners();
  initialize();
  

  return {
    userInfo,
    tokenInfo,
    isLoading,
    error,
    images,
    videos,
    isAuthenticated,
    
    initialize,
    fetchUserInfo,
    loginCallback,
    logout,
    setUserInfo,
    getUserInfo,
    forceLogout,
    refreshPoints,
    refreshUserInfo,
  };
}); 